import { useEffect, useRef } from "react";
import { toast } from "sonner";
import SendEmailDialog from "@/components/customers/SendEmailDialog";
import { useSaveFollowUpDraft } from "@/hooks/use-follow-up-mutations";
import { useCloseFollowUp, usePostponeFollowUp } from "@/hooks/use-close-follow-up";
import { fetchFollowUpWithRelations } from "@/hooks/use-follow-ups";
import type { FollowUpFlow } from "@/hooks/use-follow-up-flow";
import { FOLLOW_UP_DISMISS_NOTE } from "@/lib/follow-up-labels";
import { osloDateString } from "@/lib/oslo-time";
import BookMeetingFromRecord from "./BookMeetingFromRecord";
import PhoneNoteDialog, { type PhoneOutcome } from "./PhoneNoteDialog";
import NextStepDialog from "./NextStepDialog";
import PostponeDialog from "./PostponeDialog";
import CloseFollowUpDialog from "./CloseFollowUpDialog";

/**
 * Dialogene for utfallene på en oppfølging. Én instans per flate (køen,
 * kundekortets «Neste steg»), styrt av useFollowUpFlow. Oppfølgingen lukkes
 * først når neste steg er lagret, slik RPC-en krever. Samtalens «Hva ble
 * avtalt?» (VIB-2958) gir neste steg direkte: møtet, en e-post i dag, eller ny
 * samtale.
 */
const DAY_MS = 86_400_000;
export default function FollowUpFlowDialogs({ flow }: { flow: FollowUpFlow }) {
  const close = useCloseFollowUp();
  const postpone = usePostponeFollowUp();
  const saveDraft = useSaveFollowUpDraft();
  /** Satt når møtet er booket, så lukkingen av bookingdialogen ikke går tilbake til samtalen. */
  const booked = useRef(false);
  const { step } = flow;

  // Avvis lukker med en gang, uten dialog og uten grunn.
  const dismissing = step.kind === "dismiss" ? step.followUp : null;
  useEffect(() => {
    if (!dismissing) return;
    close.mutate(
      { followUp: dismissing, outcome: "closed", note: FOLLOW_UP_DISMISS_NOTE },
      {
        onSuccess: () => {
          toast.success("Oppfølgingen er avvist");
          flow.finish(dismissing.id);
        },
        onError: () => flow.cancel("dismiss"),
      },
    );
    // close og flow er nye objekter hver render; avvisningen skal bare sendes én gang per oppfølging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissing?.id]);

  if (step.kind === "idle" || step.kind === "dismiss") return null;
  const { followUp } = step;
  const customerName = followUp.customer?.name ?? "kunden";
  const onOpenChange = (kind: typeof step.kind) => (open: boolean) => {
    if (!open) flow.cancel(kind);
  };

  switch (step.kind) {
    case "email":
      return (
        <SendEmailDialog
          open
          onOpenChange={onOpenChange("email")}
          customerId={followUp.customer_id}
          defaultContactId={followUp.contact_id}
          defaultSubject={followUp.draft_subject ?? ""}
          defaultBody={followUp.draft_body ?? ""}
          opportunityId={followUp.opportunity_id}
          followUpId={followUp.id}
          onSent={(activityId) => flow.emailSent(followUp, activityId)}
          draft={{
            autoGenerate: true,
            onGenerated: (d) => saveDraft.mutate({ followUp, subject: d.subject, body: d.body, model: d.model }),
          }}
        />
      );
    case "phone": {
      const onOutcome = (outcome: PhoneOutcome, note: string | null, contactId: string | null) => {
        if (outcome === "meeting" || outcome === "call_again" || outcome === "not_relevant") {
          flow.phoneOutcome(followUp, note, outcome);
        } else if (outcome === "no_answer") {
          postpone.mutate(
            { followUp, newDue: osloDateString(new Date(Date.now() + DAY_MS)), note: note ? `Fikk ikke svar. ${note}` : "Fikk ikke svar" },
            { onSuccess: () => { toast.success("Ny samtale i morgen"); flow.finish(followUp.id); } },
          );
        } else {
          // E-post etter samtalen: samtalen lukkes med en e-post i dag som neste steg, og e-postdialogen åpnes på den.
          close.mutate(
            {
              followUp,
              outcome: "phone_done",
              note,
              next: { title: "Send e-post etter samtalen", due_date: osloDateString(new Date()), priority: "asap", channel: "email", contact_id: contactId ?? followUp.contact_id },
            },
            {
              onSuccess: async (result) => {
                flow.finish(followUp.id);
                if (!result.next_id) return;
                try {
                  flow.start("email", await fetchFollowUpWithRelations(result.next_id));
                } catch {
                  toast.info("Samtalen er lagret. E-posten ligger øverst i køen.");
                }
              },
            },
          );
        }
      };
      return (
        <PhoneNoteDialog
          open
          onOpenChange={onOpenChange("phone")}
          customerId={followUp.customer_id}
          customerName={customerName}
          defaultContactId={followUp.contact_id}
          customerPhone={followUp.customer?.contact_phone ?? null}
          defaultNote={flow.draftNote(followUp.id)}
          pending={close.isPending || postpone.isPending}
          onOutcome={onOutcome}
        />
      );
    }
    case "book":
      return (
        <BookMeetingFromRecord
          open
          onOpenChange={(open) => {
            if (open) return;
            if (booked.current) {
              booked.current = false;
              return;
            }
            // Lukket uten booking: tilbake til samtalen med notatet.
            flow.cancel("book");
            flow.start("phone", followUp);
          }}
          customerId={followUp.customer_id}
          name={customerName}
          opportunityId={followUp.opportunity_id}
          defaultContactId={followUp.contact_id}
          topic={followUp.title}
          defaultInternalNote={step.note}
          onBooked={(_result, meeting) => {
            booked.current = true;
            flow.finish(followUp.id);
            close.mutate(
              {
                followUp,
                outcome: "phone_done",
                note: step.note,
                next: { title: `Møte: ${meeting.title}`, due_date: osloDateString(meeting.start), priority: "this_week", channel: "meeting", contact_id: followUp.contact_id },
              },
              { onSuccess: () => toast.success("Samtalen er lagret, og møtet er neste steg") },
            );
          }}
        />
      );
    case "next":
      return (
        <NextStepDialog
          open
          onOpenChange={(open) => {
            if (!open && step.outcome === "email_sent") {
              toast.info("E-posten er sendt. Oppfølgingen lukkes når du har lagret neste steg.");
            }
            onOpenChange("next")(open);
          }}
          customerId={followUp.customer_id}
          customerName={customerName}
          defaultContactId={followUp.contact_id}
          defaultChannel={step.preset?.channel}
          defaultTitle={step.preset?.title}
          pending={close.isPending}
          onSubmit={(next) =>
            close.mutate(
              { followUp, outcome: step.outcome, note: step.note, next, activityId: step.activityId },
              {
                onSuccess: () => {
                  toast.success("Oppfølgingen er lukket");
                  flow.finish(followUp.id);
                },
              },
            )
          }
        />
      );
    case "postpone":
      return (
        <PostponeDialog
          open
          onOpenChange={onOpenChange("postpone")}
          currentDue={followUp.due_date}
          pending={postpone.isPending}
          onSubmit={({ newDue, note }) =>
            postpone.mutate(
              { followUp, newDue, note },
              {
                onSuccess: () => {
                  toast.success("Oppfølgingen er utsatt");
                  flow.finish(followUp.id);
                },
              },
            )
          }
        />
      );
    case "close":
      return (
        <CloseFollowUpDialog
          open
          onOpenChange={onOpenChange("close")}
          defaultReason={step.note}
          pending={close.isPending}
          onSubmit={(reason) =>
            close.mutate(
              { followUp, outcome: "closed", note: reason },
              {
                onSuccess: () => {
                  toast.success("Oppfølgingen er avsluttet");
                  flow.finish(followUp.id);
                },
              },
            )
          }
        />
      );
  }
}
